import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  fetchForecastLines,
  fetchAvailableDates,
  fetchTemperatures,
  fetchForecastWindow
} from './api';
import DateSlider from './components/DateSlider';
import ForecastChart from './components/ForecastChart';
import TempCostTimelineChart from './components/TempCostTimelineChart';
import FutureTemperatureTable from './components/FutureTemperatureTable';
import CalendarDatePicker from './components/CalendarDatePicker';
import ThemeToggle from './components/ThemeToggle';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://power.gailtechnology.com/api';

function isValidISODate(str) {
  return typeof str === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(str);
}

// Helper function to get date string in CST
function getDateStringCST(offsetDays = 0) {
  const now = new Date();
  // Get CST time (UTC-6)
  const cstOffset = -6 * 60; // CST is UTC-6 in minutes
  const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
  const cstTime = new Date(utcTime + (cstOffset * 60000));

  if (offsetDays !== 0) {
    cstTime.setDate(cstTime.getDate() + offsetDays);
  }

  return cstTime.getFullYear() + '-' +
         String(cstTime.getMonth() + 1).padStart(2, '0') + '-' +
         String(cstTime.getDate()).padStart(2, '0');
}

async function fetchForecastRange(targetDate) {
  const data = await fetchForecastLines(targetDate);
  const out = {};
  if (data.sheets) {
    data.sheets.forEach(sheet => {
      if (sheet.forecast_for_date && isValidISODate(sheet.sheet_date)) {
        out[sheet.sheet_date] = sheet.forecast_for_date;
      }
    });
  }
  if (data.actuals && Object.keys(data.actuals).length) {
    out['__actuals__'] = data.actuals;
  }
  if (data.final_from_next_day && Object.keys(data.final_from_next_day).length) {
    out['__final_forecast__'] = data.final_from_next_day;
  }
  return out;
}

function pearsonCorrelation(arr1, arr2) {
  const n = arr1.length;
  if (!n) return null;
  const mean1 = arr1.reduce((a, b) => a + b, 0) / n;
  const mean2 = arr2.reduce((a, b) => a + b, 0) / n;
  let num = 0, den1 = 0, den2 = 0;
  for (let i = 0; i < n; i++) {
    const d1 = arr1[i] - mean1;
    const d2 = arr2[i] - mean2;
    num += d1 * d2;
    den1 += d1 * d1;
    den2 += d2 * d2;
  }
  if (!den1 || !den2) return null;
  return num / Math.sqrt(den1 * den2);
}

function App() {
  // — Dark/Light Mode (GAIL Technology unified theme) —
  const getInitialTheme = () => {
    const saved = localStorage.getItem('gail-theme');
    if (saved === 'dark' || saved === 'light') return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  };
  const [theme, setTheme] = useState(getInitialTheme);
  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  // — Main state —
  const [timelineDates, setTimelineDates] = useState([]);
  const [targetDate, setTargetDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [forecastLines, setForecastLines] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [forecastWindow, setForecastWindow] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [forecastWindowLoading, setForecastWindowLoading] = useState(false);
  const [temperatureData, setTemperatureData] = useState([]);
  const [highestActuals, setHighestActuals] = useState(null);
  const [dataType, setDataType] = useState('lrz4');
  const [futureTemps, setFutureTemps] = useState([]);
  const [monthlyHigh, setMonthlyHigh] = useState(null);

  // — Theme effect —
  useEffect(() => {
    document.body.className = theme === 'dark' ? 'dark-mode' : '';
    localStorage.setItem('gail-theme', theme);
  }, [theme]);

  // Fetch download status on mount
  useEffect(() => {
    async function fetchDownloadStatus() {
      try {
        const res = await fetch(`${API_BASE}/download_status`);
        if (res.ok) {
          const data = await res.json();
          setDownloadStatus(data);
        }
      } catch (error) {
        console.error('Error fetching download status:', error);
      }
    }
    fetchDownloadStatus();
  }, []);

  // Fetch 15-day future highs on mount
  useEffect(() => {
    async function fetchFutureHighs() {
      try {
        const res = await fetch(`${API_BASE}/future_highs`);
        if (res.ok) {
          const data = await res.json();
          setFutureTemps(Array.isArray(data) ? data : []);
        } else {
          setFutureTemps([]);
        }
      } catch {
        setFutureTemps([]);
      }
    }
    fetchFutureHighs();
  }, []);

  // — On mount: load available dates & pick initial targetDate —
  useEffect(() => {
    async function initDates() {
      try {
        const dates = Array.isArray(await fetchAvailableDates())
          ? await fetchAvailableDates()
          : [];
        setTimelineDates(dates);
        if (dates.length) {
          // Use CST date
          const today = getDateStringCST();
          const selectedDate = dates.includes(today) ? today : dates[dates.length - 1];
          setTargetDate(selectedDate);
        }
      } catch (error) {
        console.error('Error fetching dates:', error);
        setTimelineDates([]);
      }
    }
    initDates();
  }, []);

  // — Fetch forecast lines whenever targetDate changes —
  useEffect(() => {
    if (!targetDate) return;
    setLoading(true);
    fetchForecastLines(targetDate)
      .then(data => {
        // Process forecast data
        return fetchForecastRange(targetDate);
      })
      .then(out => {
        setForecastLines(out);
      })
      .finally(() => setLoading(false));
  }, [targetDate]);

  // — Fetch forecast window (5-day) —
  useEffect(() => {
    if (!targetDate) return;
    setForecastWindowLoading(true);

    const today = getDateStringCST();

    // If viewing a past date, fetch that date's sheet directly to get its actuals
    // If viewing today or future, fetch today's sheet
    const sheetToFetch = targetDate < today ? targetDate : today;

    fetchForecastWindow(sheetToFetch)
      .then(fw => {
        if (fw && fw.forecasts) {
          setForecastWindow({
            ...fw,
            selected_date: targetDate
          });
        } else {
          setForecastWindow(fw);
        }
      })
      .catch(() => {
        // Fallback: try yesterday's sheet
        const yesterdayStr = getDateStringCST(-1);
        return fetchForecastWindow(yesterdayStr)
          .then(fw => {
            if (fw && fw.forecasts) {
              setForecastWindow({
                ...fw,
                selected_date: targetDate
              });
            } else {
              setForecastWindow(fw);
            }
          })
          .catch(() => {
            // Try two days ago as last resort
            const twoDaysAgoStr = getDateStringCST(-2);
            return fetchForecastWindow(twoDaysAgoStr)
              .then(fw => {
                if (fw && fw.forecasts) {
                  setForecastWindow({
                    ...fw,
                    selected_date: targetDate
                  });
                } else {
                  setForecastWindow(fw);
                }
              })
              .catch(() => setForecastWindow(null));
          });
      })
      .finally(() => setForecastWindowLoading(false));
  }, [targetDate]);

  // — Fetch temperatures for the day —
  useEffect(() => {
    if (!targetDate) return;
    fetchTemperatures(targetDate)
      .then(res => setTemperatureData(res.temperatures || []))
      .catch(() => setTemperatureData([]));
  }, [targetDate]);

  // — Fetch highest actuals for the month —
  useEffect(() => {
    if (!targetDate) return;
    const month = targetDate.slice(0, 7);
    axios
      .get(`${API_BASE}/highest_actuals/${month}`)
      .then(res => setHighestActuals(res.data))
      .catch(() => setHighestActuals(null));
  }, [targetDate]);

  // — Patch in final forecast if it's not yet in forecastLines —
  useEffect(() => {
    if (!targetDate || forecastLines.__final_forecast__) return;
    fetchForecastLines(targetDate).then(data => {
      if (data.final_forecast && Object.keys(data.final_forecast).length) {
        setForecastLines(prev => ({
          ...prev,
          __final_forecast__: data.final_forecast
        }));
      }
    });
  }, [forecastLines, targetDate]);

  // — Prepare chart/table data —
  const chartTableData = useMemo(() => {
    if (!targetDate) return [];
    const rows = [];

    // 1. All forecast sheets with actuals merged in
    Object.entries(forecastLines).forEach(([sd, block]) => {
      if (sd === '__actuals__' || sd === '__final_forecast__') return;
      Object.entries(block || {}).forEach(([hour, vals]) => {
        // Get the actual value for this hour if it exists
        let actualValue = null;
        if (forecastLines.__actuals__ && forecastLines.__actuals__[hour]) {
          actualValue = dataType === 'miso' 
            ? forecastLines.__actuals__[hour].MISO_actual 
            : forecastLines.__actuals__[hour].LRZ4_actual;
        }
        
        rows.push({
          forecast_date: sd,
          target_date: targetDate,
          hour: Number(hour),
          forecast: dataType === 'miso' ? vals.MISO : vals.LRZ4,
          actuals: actualValue
        });
      });
    });

    // 2. Final Forecast with actuals merged in
    if (forecastLines.__final_forecast__) {
      Object.entries(forecastLines.__final_forecast__).forEach(([hour, vals]) => {
        // Get the actual value for this hour if it exists
        let actualValue = null;
        if (forecastLines.__actuals__ && forecastLines.__actuals__[hour]) {
          actualValue = dataType === 'miso' 
            ? forecastLines.__actuals__[hour].MISO_actual 
            : forecastLines.__actuals__[hour].LRZ4_actual;
        }
        
        rows.push({
          forecast_date: 'Final Forecast',
          target_date: targetDate,
          hour: Number(hour),
          forecast: dataType === 'miso' ? vals.MISO : vals.LRZ4,
          actuals: actualValue
        });
      });
    }

    // 3. Merge temperature
    return rows
      .filter(r => true) // could filter by type if needed
      .map(r => {
        const t = temperatureData.find(t => Number(t.hour) === r.hour);
        return { ...r, temperature: t?.temperature };
      });
  }, [forecastLines, dataType, targetDate, temperatureData]);

  // — Correlation actuals vs. temp —
  const correlation = useMemo(() => {
    const filt = chartTableData.filter(
      r => typeof r.actuals === 'number' && typeof r.temperature === 'number'
    );
    const acts = filt.map(r => r.actuals);
    const temps = filt.map(r => r.temperature);
    return acts.length && temps.length ? pearsonCorrelation(acts, temps) : null;
  }, [chartTableData]);

  // — Month switcher helpers —
  const monthsInTimeline = Array.from(
    new Set(timelineDates.filter(isValidISODate).map(d => d.slice(0, 7)))
  );
  const goToMonth = month => {
    const inMonth = timelineDates.filter(d => d.startsWith(month)).sort();
    if (inMonth.length) setTargetDate(inMonth[inMonth.length - 1]);
  };
  const renderMonthSwitcher = () => {
    if (monthsInTimeline.length <= 1) return null;
    return (
      <div style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong>Jump to Month:</strong>
        <select
          value={targetDate.slice(0, 7)}
          onChange={e => goToMonth(e.target.value)}
          style={{ padding: '4px 12px', borderRadius: 4, border: '1px solid #ccc', fontWeight: 'bold', fontSize: 16 }}
        >
          {monthsInTimeline.map(m => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>
    );
  };

  // — Fetch monthly high from backend API —
  useEffect(() => {
    console.log('Monthly high useEffect running, targetDate:', targetDate, 'dataType:', dataType);
    if (!targetDate) return;
    const month = targetDate.slice(0, 7);
    const url = `${API_BASE}/monthly_high/${month}`;
    console.log('Fetching monthly high from:', url);

    axios.get(url)
      .then(res => {
        console.log('Monthly high response:', res.data);
        // Store as object with value, date, and hour for tooltip display
        const isLrz4 = dataType === 'lrz4';
        const highData = {
          value: isLrz4 ? res.data.lrz4_high : res.data.miso_high,
          date: isLrz4 ? res.data.lrz4_high_date : res.data.miso_high_date,
          hour: isLrz4 ? res.data.lrz4_high_hour : res.data.miso_high_hour
        };
        console.log(`Fetched monthly high for ${month}:`, highData);
        setMonthlyHigh(highData);
      })
      .catch(err => {
        console.error('Failed to fetch monthly high:', err);
        setMonthlyHigh(null);
      });
  }, [targetDate, dataType]);

  // — Calculate highest forecasted values from current perspective —
  const monthlyHighsFromPerspective = useMemo(() => {
    if (!forecastWindow || !forecastWindow.forecasts) return null;
    
    const results = [];
    const forecasts = forecastWindow.forecasts;
    const currentMonth = targetDate.slice(0, 7);
    
    let maxFound = 0;
    
    // Collect ALL forecast values from the 5-day window, regardless of month
    Object.keys(forecasts).forEach(date => {
      Object.keys(forecasts[date]).forEach(hour => {
        const val = forecasts[date][hour] && forecasts[date][hour][dataType.toUpperCase()];
        if (typeof val === 'number' && !isNaN(val)) {
          if (val > maxFound) maxFound = val;
          results.push({
            date,
            hour: parseInt(hour),
            value: val,
            type: dataType,
            temperature: null,
            isForecast: true
          });
        }
      });
    });
    
    // Also add ALL data from chartTableData for dates in current month
    if (chartTableData.length > 0) {
      chartTableData.forEach(row => {
        // Include data for current month dates
        if (row.target_date && row.target_date.slice(0, 7) === currentMonth) {
          // Prefer actuals over forecasts
          const value = row.actuals !== null ? row.actuals : row.forecast;
          if (value !== null && typeof value === 'number') {
            // Check if we already have this date/hour combo
            const existingIndex = results.findIndex(r => r.date === row.target_date && r.hour === row.hour);
            if (existingIndex >= 0) {
              // Update if this is an actual value (prefer actuals over forecasts)
              if (row.actuals !== null) {
                results[existingIndex] = {
                  date: row.target_date,
                  hour: row.hour,
                  value: row.actuals,
                  type: dataType,
                  temperature: row.temperature || null,
                  isForecast: false
                };
              }
            } else {
              // Add new entry
              results.push({
                date: row.target_date,
                hour: row.hour,
                value: value,
                type: dataType,
                temperature: row.temperature || null,
                isForecast: row.actuals === null
              });
            }
          }
        }
      });
    }
    
    // Sort by value descending and take top 15
    results.sort((a, b) => b.value - a.value);
    return results.slice(0, 15);
  }, [forecastWindow, targetDate, dataType, chartTableData]);

  // — Prior Years Monthly Highs Table —
  const renderPriorYearsHighs = () => {
    if (!highestActuals?.results) return null;
    
    const currentYear = new Date().getFullYear();
    const monthName = new Date(targetDate + '-01').toLocaleDateString('en-US', { month: 'long' });
    
    // Filter for the selected data type
    const filtered = highestActuals.results.filter(r => 
      (r.type || '').toLowerCase() === dataType.toLowerCase()
    );
    
    // Group by year and get the highest value for each year
    const yearlyHighs = {};
    filtered.forEach(r => {
      const year = r.date.slice(0, 4);
      if (!yearlyHighs[year] || r.value > yearlyHighs[year].value) {
        yearlyHighs[year] = r;
      }
    });
    
    // Get current year and prior years
    const thisYear = yearlyHighs[currentYear];
    const priorYears = Object.entries(yearlyHighs)
      .filter(([year]) => year < currentYear)
      .sort(([a], [b]) => b - a)
      .map(([_, data]) => data);
    
    if (!thisYear && priorYears.length === 0) return null;
    
    return (
      <div style={{ marginTop: 20, marginBottom: 20 }}>
        <h3>{monthName} {dataType.toUpperCase()} Records</h3>
        <table style={{ borderCollapse: 'collapse', marginBottom: 8 }}>
          <tbody>
            {thisYear && (
              <tr>
                <td style={{ padding: '4px 12px' }}><strong>{currentYear}:</strong></td>
                <td style={{ padding: '4px 12px' }}>{thisYear.value.toFixed(1)}</td>
                <td style={{ padding: '4px 12px' }}>on {thisYear.date} at {thisYear.hour}:00</td>
              </tr>
            )}
            {priorYears.slice(0, 2).map(r => (
              <tr key={r.date}>
                <td style={{ padding: '4px 12px' }}><strong>{r.date.slice(0, 4)}:</strong></td>
                <td style={{ padding: '4px 12px' }}>{r.value.toFixed(1)}</td>
                <td style={{ padding: '4px 12px' }}>on {r.date} at {r.hour}:00</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // — Highest Actuals table —
  const renderHighestActuals = () => {
    console.log('renderHighestActuals - highestActuals:', highestActuals);
    if (!highestActuals?.results?.length) {
      console.log('No highestActuals.results, returning null');
      return null;
    }
    const filtered = highestActuals.results.filter(
      r => (r.type || '').toLowerCase() === dataType.toLowerCase()
    );
    const grouped = filtered.reduce((acc, r) => {
      const yr = r.date.slice(0, 4);
      acc[yr] = acc[yr] || [];
      acc[yr].push(r);
      return acc;
    }, {});
    const rows = Object.entries(grouped)
      .flatMap(([yr, arr]) => arr.map(r => ({ ...r, year: yr })))
      .sort((a, b) =>
        b.year - a.year ||
        b.date.localeCompare(a.date) ||
        b.hour - a.hour
      );

    return (
      <div style={{ marginTop: 32 }}>
        <h2>Historical Top Of The Month</h2>
        <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 8 }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ccc', padding: 4 }}>Year</th>
              <th style={{ border: '1px solid #ccc', padding: 4 }}>Date</th>
              <th style={{ border: '1px solid #ccc', padding: 4 }}>Hour</th>
              <th style={{ border: '1px solid #ccc', padding: 4 }}>Value</th>
              <th style={{ border: '1px solid #ccc', padding: 4 }}>Temperature (°F)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={{ border: '1px solid #ccc', padding: 4 }}>{r.year}</td>
                <td style={{ border: '1px solid #ccc', padding: 4 }}>{r.date.slice(5)}</td>
                <td style={{ border: '1px solid #ccc', padding: 4 }}>{r.hour}</td>
                <td style={{ border: '1px solid #ccc', padding: 4 }}>{r.value}</td>
                <td style={{ border: '1px solid #ccc', padding: 4 }}>
                  {r.temperature ?? ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <ThemeToggle theme={theme} onToggle={toggleTheme} />

      <div style={{ maxWidth: 900, margin: '0 auto', padding: 24, minHeight: '100vh' }}>
        <h1>Power Forecasts &amp; Actuals</h1>
        <div style={{ marginBottom: 12 }}>
          <a href="/curtailment_time_conversion.xlsx" download style={{ fontSize: 14, color: "#4a9eff" }}>
            📥 Curtailment Time Conversion Sheet
          </a>
        </div>
        


        <div style={{ marginBottom: 16 }}>
          <CalendarDatePicker
            availableDates={timelineDates}
            selectedDate={targetDate}
            onChange={setTargetDate}
          />
        </div>

        {renderMonthSwitcher()}

        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() => setDataType('lrz4')}
            style={{ marginRight: 8, fontWeight: dataType === 'lrz4' ? 'bold' : 'normal' }}
          >
            LRZ4
          </button>
          <button
            onClick={() => setDataType('miso')}
            style={{ fontWeight: dataType === 'miso' ? 'bold' : 'normal' }}
          >
            MISO
          </button>
        </div>

        <DateSlider
          dates={timelineDates}
          selectedDate={targetDate}
          onChange={setTargetDate}
          loading={loading}
        />

        {loading ? (
          <div>Loading...</div>
        ) : chartTableData.length > 0 ? (
          <>
            <ForecastChart data={chartTableData} targetDate={targetDate} dataType={dataType} forecastWindow={forecastWindow} monthlyHigh={monthlyHigh} theme={theme} />
            <TempCostTimelineChart data={chartTableData} correlation={correlation} />
            {renderHighestActuals()}
            <FutureTemperatureTable data={futureTemps} highlightColor="crimson" />
          </>
        ) : (
          <p>No data for this date.</p>
        )}
        
        {/* Download Status Display */}
        {downloadStatus && (
          <div style={{ 
            marginTop: 40, 
            textAlign: 'center', 
            fontSize: '11px', 
            color: theme === 'dark' ? '#666' : '#999',
            opacity: 0.7
          }}>
            Last file retrieved: {downloadStatus.last_download_time}<br/>
            Last file name: {downloadStatus.last_filename}
          </div>
        )}
      </div>
    </>
  );
}

export default App;
